import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { workbooksApi } from "../api/workbooks";
import { catalogueApi } from "../api/catalogue";
import Navbar from "../components/Navbar";
import { useWorkbookBundles } from "../data/workbookBundles";
import WorkbookTrackCard, { WORKBOOK_TRACK_CARD_CSS } from "../components/WorkbookTrackCard";
import VideoPlayer from "../components/VideoPlayer";

const C = { cream:"#F5F2EC", card:"#FFFFFF", navy:"#1a1a2e", navySoft:"#1e2a3a", blue:"#2563EB", orange:"#EA580C", line:"#E4DFD4", muted:"#6B6458" };

export default function WorkbookCatalog() {
  const navigate = useNavigate();
  const [workbooks, setWorkbooks] = useState([]);
  const [ownedBundleSlugs, setOwnedBundleSlugs] = useState([]);
  const [bundleStats, setBundleStats] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [marquee, setMarquee] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      workbooksApi.getAll().catch(() => null),
      catalogueApi.getBundles().catch(() => null),
      workbooksApi.getMarquee().catch(() => null),
    ]).then(([all, bundles, rev]) => {
      if (!mounted) return;
      if (all) {
        setWorkbooks(all.workbooks ?? []);
        setOwnedBundleSlugs(all.ownedBundleSlugs ?? []);
        setBundleStats(all.bundleStats ?? null);
      }
      if (bundles) setCatalogue(bundles);
      if (rev) setMarquee(rev.reviews ?? []);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Bundle view models (copy, counts, ownership, ratings, enrolled, price) are
  // derived by the shared helper so this page and My Courses stay in lockstep.
  const bundles = useWorkbookBundles({
    bundles: catalogue,
    workbooks,
    ownedBundleSlugs,
    bundleStats,
  });

  // Owned bundles open their library; unowned bundles open the detail page.
  const onOpen = (b) =>
    navigate(
      b.owned
        ? `/workbooks/bundle/${b.track}/library`
        : `/workbooks/bundle/${b.track}`,
    );

  const ownedBundles = bundles.filter((b) => b.owned);
  // All tracks is the store: only bundles the user does not own.
  const unownedBundles = bundles.filter((b) => !b.owned);

  // Shared card so the My workbooks and All tracks rows stay identical, and so
  // My Courses renders the very same card.
  const renderCard = (b) => (
    <WorkbookTrackCard key={b.track} bundle={b} onOpen={() => onOpen(b)} />
  );

  if (loading) {
    return (
      <div
        style={{
          background: C.cream,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: C.muted,
          fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div style={{ background:C.cream, minHeight:"100vh", color:C.navy, fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif" }}>
      <Navbar />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        ${WORKBOOK_TRACK_CARD_CSS}
        @keyframes sc-marquee{from{transform:translateX(0);}to{transform:translateX(-50%);}}
        .sc-marquee-track{display:flex;width:max-content;animation-name:sc-marquee;animation-timing-function:linear;animation-iteration-count:infinite;}
        .sc-marquee-mask:hover .sc-marquee-track{animation-play-state:paused;}
        @media(max-width:820px){.grid{grid-template-columns:1fr !important;}}
      `}</style>
      <div style={{ maxWidth:1120, margin:"0 auto", padding:"56px 24px 80px" }}>
        <div style={{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:C.orange, marginBottom:14 }}>Learning Library</div>
        <h1 className="frns" style={{ fontSize:46, fontWeight:600, lineHeight:1.08, margin:"0 0 14px", maxWidth:680 }}>
          Pick the track you want to <span style={{ fontStyle:"italic", color:C.blue }}>automate</span>
        </h1>
        <p style={{ fontSize:18, color:C.muted, maxWidth:580, margin:"0 0 44px", lineHeight:1.5 }}>
          Every track starts from the C# foundation, then moves into the real CAD API. Read the free sections before you decide.
        </p>
        {/* Video band */}
        <div style={{ maxWidth:860, margin:"36px auto 48px" }}>
          <div style={{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:C.orange, marginBottom:14 }}>
            Watch &middot; CAD Automation in Action
          </div>
          {/* Same Plyr player the course lessons use: matching controls and no
              download affordance. Public R2 URL, so no refreshSrc is needed. */}
          <div style={{ width:"100%", background:C.navy, borderRadius:14, overflow:"hidden" }}>
            <VideoPlayer src="https://pub-b8eecc60204945e297a8e070748ebf5b.r2.dev/c1a89bb8-aa9f-44d2-a3fb-433bd6f79248.mp4" />
          </div>
        </div>
        <hr style={{ border:"none", borderTop:`1px solid ${C.line}`, margin:"0 0 48px" }} />

        {/* My workbooks band: owned, tinted inset panel */}
        {ownedBundles.length > 0 && (
          <div style={{ background:"#FBF9F4", border:`1px solid ${C.line}`, borderRadius:16, padding:28, marginBottom:40 }}>
            <h2 className="frns" style={{ fontSize:28, fontWeight:600, lineHeight:1.1, margin:"0 0 4px" }}>My workbooks</h2>
            <div style={{ fontSize:14, color:C.muted, marginBottom:18 }}>Tracks you own</div>
            <div className="grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:22 }}>
              {ownedBundles.map(renderCard)}
            </div>
          </div>
        )}

        {/* All tracks band: the store, unowned only */}
        {unownedBundles.length > 0 && (
          <>
            <h2 className="frns" style={{ fontSize:28, fontWeight:600, lineHeight:1.1, margin:"0 0 4px" }}>All tracks</h2>
            <div style={{ fontSize:14, color:C.muted, marginBottom:18 }}>Browse and unlock more</div>
            <div className="grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:22 }}>
              {unownedBundles.map(renderCard)}
            </div>
          </>
        )}

        {/* Reviews marquee: recent visible reviews (text or rating-only), looping. Last section. */}
        {marquee.length > 0 && (() => {
          // Dedupe by id (keep natural order) so the same review never repeats
          // more than the seamless loop strictly requires.
          const seen = new Set();
          const unique = marquee.filter((r) => {
            if (r.id == null) return true;
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });

          // Repeat only enough to cover the viewport plus one buffer card, then
          // mirror the set once so the -50% translate lands on an identical frame.
          const CARD_UNIT = 318; // 300 width + 18 marginRight
          const viewport = typeof window !== "undefined" ? window.innerWidth : 1280;
          const cardsToFill = Math.ceil(viewport / CARD_UNIT) + 1;
          const repeat = Math.max(1, Math.ceil(cardsToFill / unique.length));
          const oneSet = Array.from({ length: repeat }, () => unique).flat();
          const loop = [...oneSet, ...oneSet];
          const initialsOf = (name = "") =>
            name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
          return (
            <section style={{ marginTop: 56 }}>
              <div style={{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:C.orange, marginBottom:16 }}>
                Trusted by engineers
              </div>

              <div className="sc-marquee-mask" style={{ overflow:"hidden", width:"100%" }}>
                <div className="sc-marquee-track" style={{ animationDuration: `${oneSet.length * 6}s` }}>
                  {loop.map((r, i) => (
                    <div key={i} style={{ flexShrink:0, width:300, minHeight:150, marginRight:18, background:C.card, border:`1px solid ${C.line}`, borderRadius:12, padding:18, display:"flex", flexDirection:"column" }}>
                      <div style={{ display:"flex", gap:2, marginBottom:10 }}>
                        {[1,2,3,4,5].map((n) => (
                          <span key={n} style={{ fontSize:14, color: n <= r.rating ? C.orange : "#e0d8c8" }}>★</span>
                        ))}
                      </div>
                      {r.text && r.text.trim() !== "" ? (
                        <p style={{ fontSize:14, lineHeight:1.5, color:"#2c2c3a", margin:"0 0 16px", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                          {r.text}
                        </p>
                      ) : (
                        <p style={{ fontSize:13, lineHeight:1.5, color:C.muted, margin:"0 0 16px", fontStyle:"italic" }}>
                          Rated {r.rating} of 5
                        </p>
                      )}
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:"auto" }}>
                        <div style={{ flexShrink:0, width:36, height:36, borderRadius:"50%", background:C.navy, color:"#fff", display:"grid", placeItems:"center", fontSize:13, fontWeight:700 }}>
                          {initialsOf(r.name)}
                        </div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:13.5, fontWeight:700, color:C.navy, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.name}</div>
                          {r.role && <div style={{ fontSize:12, color:C.muted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.role}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}
      </div>
    </div>
  );
}
